import {Component, NgZone} from "@angular/core";
import {OneSignal} from "@ionic-native/onesignal";
import {SplashScreen} from "@ionic-native/splash-screen";
import {StatusBar} from "@ionic-native/status-bar";
import {AlertController, App, Events, Platform} from "ionic-angular";
import 'rxjs/add/operator/first';
import {AuthorizedService} from "../services/authorized.service";
import {DocumentService} from "../services/document.service";
import {NewsletterService} from "../services/newsletter.service";
import {NotificationService} from "../services/notification.service";
import {PhotoService} from "../services/photo.services";
import {PushSubscriptionService} from "../services/push-subscription.service";
import {UrlOpenerProvider} from "../services/url-opener.service";

@Component({
  templateUrl: 'app.html'
})
export class MyApp {
  public rootPage: any = 'HomePage';
  public showLoader: boolean;
  public config: any = {};

  constructor(ngZone: NgZone,
              private app: App,
              events: Events,
              platform: Platform,
              statusBar: StatusBar,
              splashScreen: SplashScreen,
              oneSignal: OneSignal,
              pushSubscriptionService: PushSubscriptionService,
              private authorizedService: AuthorizedService,
              documentService: DocumentService,
              newsletterService: NewsletterService,
              notificationService: NotificationService,
              photoService: PhotoService,
              alertCtrl: AlertController,
              private urlOpener: UrlOpenerProvider) {
    this.showLoader = true;

    this.authorizedService.loadAppConfig().first()
      .subscribe((appConfig) => {
        this.config = appConfig;

        this.authorizedService.statusChange.first()
          .subscribe((loggedIn) => {
            platform.ready().then(() => {
              if (platform.is('cordova')) {
                statusBar.styleDefault();
                statusBar.overlaysWebView(false); // fix for overlapping in IOS

                if (appConfig.onesignal_app_id) {
                  oneSignal.startInit(appConfig.onesignal_app_id);
                  oneSignal.inFocusDisplaying(oneSignal.OSInFocusDisplayOption.Notification);
                  oneSignal.handleNotificationReceived().subscribe(
                    () => events.publish('notification:received')
                  );
                  oneSignal.handleNotificationOpened().subscribe((notificationOpened) => {
                    const notification = notificationOpened.notification;

                    events.publish('notification:received');

                    if (notification) {
                      const payload = notification.payload;

                      if (payload) {
                        const data = payload.additionalData;

                        if (data) {
                          if (data.notification_id) {
                            notificationService.getOne(data.notification_id).subscribe();
                          }

                          if (data.action) {
                            switch (data.action) {
                              case 'activity':
                                this.openPage('InschrijvenForm', {itemId: data.id});
                                break;
                              case 'appointment':
                                this.openPage('GesprekPlannerForm', {code: data.code});
                                break;
                              case 'calendar':
                                this.openPage('AgendaDetail', {agendaId: data.id});
                                break;
                              case 'document':
                                this.showLoader = true;

                                documentService.getOne(data.id).subscribe(response => {
                                  if (response.file && response.file.url) {
                                    this.urlOpener.openUrl(response.file.url).finally(() => {
                                      this.showLoader = false;
                                    });
                                  } else {
                                    this.showLoader = false;
                                    alertCtrl.create({
                                      message: 'Helaas kon het bestand niet worden gevonden, controleer het later nog eens',
                                      buttons: [
                                        {
                                          text: 'OK'
                                        }
                                      ]
                                    }).present();
                                  }
                                }, () => {
                                  this.showLoader = false;
                                });
                                break;
                              case 'gallery':
                                photoService.getOne(data.id).subscribe(response => {
                                  this.openPage('FotosList', {gid: response.id, title: response.title});
                                });
                                break;
                              case 'newsletter':
                                this.showLoader = true;

                                newsletterService.getOne(data.id).subscribe(response => {
                                  if (response.file && response.file.url) {
                                    this.urlOpener.openUrl(response.file.url).finally(() => {
                                      this.showLoader = false;
                                    });
                                  } else {
                                    this.showLoader = false;
                                    alertCtrl.create({
                                      message: 'Helaas kon het bestand niet worden gevonden, controleer het later nog eens',
                                      buttons: [
                                        {
                                          text: 'OK'
                                        }
                                      ]
                                    }).present();
                                  }
                                }, () => {
                                  this.showLoader = false;
                                });
                                break;
                              case 'post':
                                this.openPage('NieuwsDetail', {itemId: data.id});
                                break;
                              case 'survey':
                                this.openPage('EnqueteForm', {enqueteId: data.id});
                                break;
                            }
                          }
                        }
                      }
                    }
                  });
                  oneSignal.endInit();

                  oneSignal.getIds().then(function ({userId}) {
                    if (userId) {
                      pushSubscriptionService.setSubscription(userId);
                    }
                  });

                  oneSignal.addSubscriptionObserver().subscribe(() => {
                    oneSignal.getIds().then(function ({userId}) {
                      pushSubscriptionService.setSubscription(userId);
                    });
                  });
                }
              } else if (appConfig.onesignal_app_id) {
                let loaded = false;

                const script = document.createElement('script') as HTMLScriptElement;
                script.async = true;
                script.defer = true;
                script.src = 'https://cdn.onesignal.com/sdks/OneSignalSDK.js';

                script.onerror = () => {
                  script.remove();
                  setTimeout(() => loaded || document.body.appendChild(script), 5000);
                };

                document.body.appendChild(script);

                (<any>window).OneSignal = (<any>window).OneSignal || [];

                (<any>window).OneSignal.push(() => {
                  loaded = true;

                  const OneSignal: any = (<any>window).OneSignal;

                  OneSignal.init({
                    appId: appConfig.onesignal_app_id,
                    autoRegister: false,
                    notifyButton: {
                      enable: false,
                    },
                    promptOptions: {
                      actionMessage: `${appConfig.name} wil u notificaties sturen om u op de hoogte te houden`,
                      acceptButtonText: "Toestaan",
                      cancelButtonText: "Nee bedankt",
                      showCredit: false,
                    },
                    welcomeNotification: {
                      disable: true,
                    },
                    persistNotification: false,
                    notificationClickHandlerMatch: 'origin',
                    notificationClickHandlerAction: 'focus',
                  });

                  OneSignal.setDefaultNotificationUrl(`${location.origin}`);
                  OneSignal.setDefaultTitle(appConfig.name);

                  OneSignal.getUserId(
                    (userId: string) => ngZone.run(() => pushSubscriptionService.setSubscription(userId))
                  );

                  const notificationListener = (notification: any) => {
                    OneSignal.addListenerForNotificationOpened(notificationListener);

                    ngZone.run(() => {
                      events.publish('notification:received');

                      const data = notification.data;
                      if (data) {
                        if (data.notification_id) {
                          notificationService.getOne(data.notification_id).subscribe();
                        }

                        if (data.action) {
                          switch (data.action) {
                            case 'activity':
                              this.openPage('InschrijvenForm', {itemId: data.id});
                              break;
                            case 'appointment':
                              this.openPage('GesprekPlannerForm', {code: data.code});
                              break;
                            case 'calendar':
                              this.openPage('AgendaDetail', {agendaId: data.id});
                              break;
                            case 'document':
                              this.showLoader = true;

                              documentService.getOne(data.id).subscribe(response => {
                                if (response.file && response.file.url) {
                                  this.urlOpener.openUrl(response.file.url).finally(() => {
                                    this.showLoader = false;
                                  });
                                } else {
                                  this.showLoader = false;
                                  alertCtrl.create({
                                    message: 'Helaas kon het bestand niet worden gevonden, controleer het later nog eens',
                                    buttons: [
                                      {
                                        text: 'OK'
                                      }
                                    ]
                                  }).present();
                                }
                              }, () => {
                                this.showLoader = false;
                              });
                              break;
                            case 'gallery':
                              photoService.getOne(data.id).subscribe(response => {
                                this.openPage('FotosList', {gid: response.id, title: response.title});
                              });
                              break;
                            case 'newsletter':
                              this.showLoader = true;

                              newsletterService.getOne(data.id).subscribe(response => {
                                if (response.file && response.file.url) {
                                  this.urlOpener.openUrl(response.file.url).finally(() => {
                                    this.showLoader = false;
                                  });
                                } else {
                                  this.showLoader = false;
                                  alertCtrl.create({
                                    message: 'Helaas kon het bestand niet worden gevonden, controleer het later nog eens',
                                    buttons: [
                                      {
                                        text: 'OK'
                                      }
                                    ]
                                  }).present();
                                }
                              }, () => {
                                this.showLoader = false;
                              });
                              break;
                            case 'post':
                              this.openPage('NieuwsDetail', {itemId: data.id});
                              break;
                            case 'survey':
                              this.openPage('EnqueteForm', {enqueteId: data.id});
                              break;
                          }
                        }
                      }
                    });
                  };

                  OneSignal.addListenerForNotificationOpened(notificationListener);

                  OneSignal.on('subscriptionChange', (subscribed: boolean) => {
                    if (subscribed) {
                      OneSignal.getUserId(
                        (userId: string) => ngZone.run(() => pushSubscriptionService.setSubscription(userId))
                      );
                    } else {
                      ngZone.run(() => pushSubscriptionService.setSubscription(null));
                    }
                  });

                  OneSignal.on('notificationDisplay', () => events.publish('notification:received'));

                  OneSignal.showHttpPrompt();
                });
              }


              const rootNav = this.app.getRootNavs()[0];
              if (appConfig.global_login && !loggedIn) {
                rootNav.setRoot('Login');
              }

              if ((appConfig.global_login && !loggedIn) || rootNav.isTransitioning()) {
                const subscription = rootNav.viewDidEnter.subscribe(() => {
                  subscription.unsubscribe();

                  if (platform.is('cordova')) {
                    splashScreen.hide();
                  }
                }, () => {
                  subscription.unsubscribe();

                  if (platform.is('cordova')) {
                    splashScreen.hide();
                  }
                });
              } else if (platform.is('cordova')) {
                splashScreen.hide();
              }

              this.showLoader = false;
            });
          });
      });
  }

  get isLoggedIn() {
    return this.authorizedService.isLoggedIn;
  }

  logout() {
    this.authorizedService.logout().subscribe(() => {
      this.authorizedService.hasGlobalLogin().subscribe((globalLogin) => {
        if (globalLogin) {
          this.app.getRootNavs()[0].setRoot('Login');
        } else {
          this.app.getRootNavs()[0].setRoot('HomePage');
        }
      });
    });
  }

  open(event: Event, page: any) {
    event.preventDefault();

    this.openPage(page);
  }

  openPage(page: any, params?: any, config?: any) {
    let requireAuth = true;

    if (typeof page === 'object') {
      requireAuth = !((page.strategy & 10) == 10) && ((page.strategy & 20) == 20);

      if ('component' in page) {
        page = page.component;
      } else if ('url' in page) {
        return this.urlOpener.openUrl(page.url, page.title);
      } else if ('file' in page) {
        return this.urlOpener.openFile(page.file, page.title);
      }
    }

    if (!requireAuth || this.authorizedService.isLoggedIn) {
      return this.app.getRootNavs()[0].push(page, params, config);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts